/**
 * DMS-FOLLOWUP-PERSISTENT-METRICS (DA-2).
 *
 * The in-process counters exposed by `getDocMatchStrictnessSummary()`
 * (DMS-5 / `strictnessTelemetry.ts`) reset every cold start. That makes
 * the admin "are we mis-classifying less today than last week?" question
 * unanswerable: a single tab refresh wipes the trend.
 *
 * This module persists those counters to Firestore at:
 *
 *   users/{uid}/dms_metrics/{dayId}
 *     dayId  = `YYYY-MM-DD` in UTC
 *     shape  = {
 *       day,                      // ISO date e.g. '2026-05-19'
 *       classifierVersion,        // MODEL_VERSION at write time
 *       scoring: {
 *         cacheHits, cacheMisses, timeouts, errors, filenameShortCircuits
 *       },
 *       applicability: {
 *         filenameNegativeSuppressed, strongClaimRejected
 *       },
 *       lastFlushedAt,            // ISO timestamp of the latest write
 *     }
 *
 * Write strategy:
 *   - Debounced 30 s — every scoring/applicability mutation calls
 *     `scheduleDmsMetricsFlush()`, which arms a single timer that fires
 *     once the queue goes quiet. Cheap (one setDoc(..., {merge:true})
 *     per 30 s of activity max).
 *   - Cumulative — we read the existing doc for the current day, add
 *     the in-process counters, write back. A reload in the same UTC
 *     day therefore stays monotonic. New day → fresh document.
 *   - Best-effort — every Firestore call is wrapped in try/catch; a
 *     write failure is logged and silently absorbed. The strictness
 *     pipeline never blocks on telemetry.
 *
 * Firestore rules sketch (deploy is a separate ops step):
 *
 *   match /users/{userId}/dms_metrics/{dayId} {
 *     // Read: owner OR admin. Admins need this to see the cross-tenant
 *     // misclassified-page rollup. Owner read covers the unlikely case
 *     // of a self-serve telemetry view.
 *     allow read: if isOwner(userId) || isAdmin();
 *
 *     // Write: ADMIN ONLY at the rules layer. The DA-2 brief explicitly
 *     // asks for admin-write-only. Non-admin sessions still call this
 *     // helper (so the in-process counters are always emitted), but the
 *     // setDoc PROMISE rejects with permission-denied for non-admins.
 *     // The try/catch swallows that — the telemetry is degraded for
 *     // non-admin sessions, which is acceptable: admins are the only
 *     // population that needs the persisted history (DA-1 page is admin
 *     // gated). Server-managed metrics-rollup writes also keep working.
 *     allow create, update: if isAdmin();
 *     allow delete: if isAdmin();
 *   }
 *
 * Loader: `loadDmsMetricsHistory(uid, days)` returns the last N days
 * (default 14) sorted oldest→newest for the DA-1 page's trend chart.
 *
 * Test seams: every Firestore I/O is dynamically imported, so unit
 * tests inject a fake `getDoc` / `setDoc` via the optional `io` arg.
 * The pure aggregation + key helpers are exported separately.
 */
import { MODEL_VERSION } from './relevanceScoring';
// Type-only import — does NOT pull `docApplicability` (and thus
// `REPORT_TYPE_REGISTRY`) into the module graph at load. The value
// reader is dynamic-imported inside `flushDmsMetrics` so AdminPanel
// test fixtures that mock `config/reportTypes` partially don't crash
// while collecting this module's tests.
import type { DocMatchStrictnessSummary } from './strictnessTelemetry';

/** Default debounce window for `scheduleDmsMetricsFlush`. */
export const DMS_METRICS_FLUSH_DEBOUNCE_MS = 30_000;

/** Default lookback window for `loadDmsMetricsHistory`. */
export const DMS_METRICS_DEFAULT_HISTORY_DAYS = 14;

const DMS_METRICS_SUBCOLLECTION = 'dms_metrics';

/* ── Shape ──────────────────────────────────────────────────────── */

export interface DmsMetricsDoc {
  /** ISO date (UTC) — `YYYY-MM-DD`. */
  day: string;
  /** MODEL_VERSION at write time. Lets the DA-1 page bucket days by
   *  classifier revision so a version bump shows up as a discontinuity. */
  classifierVersion: string;
  scoring: DocMatchStrictnessSummary['scoring'];
  applicability: DocMatchStrictnessSummary['applicability'];
  /** ISO timestamp of the latest flush. */
  lastFlushedAt: string;
}

/** Firestore I/O seam — tests inject deterministic getters/setters. */
export interface DmsMetricsIO {
  /** Resolves to the existing doc for `(uid, dayId)`, or null. */
  getDoc: (uid: string, dayId: string) => Promise<DmsMetricsDoc | null>;
  /** Persist the full doc. Merge semantics are caller-determined. */
  setDoc: (uid: string, dayId: string, value: DmsMetricsDoc) => Promise<void>;
  /** Resolves to up-to-N most recent docs for `uid`, oldest first. */
  listDocs: (uid: string, limit: number) => Promise<DmsMetricsDoc[]>;
}

/* ── Pure helpers ────────────────────────────────────────────────── */

/**
 * UTC `YYYY-MM-DD` derived from a wall-clock millisecond. Pure — the
 * UTC choice is deliberate so a single user crossing midnight in
 * different time zones doesn't split one logical day across two docs.
 */
export function dmsMetricsDayId(nowMs: number): string {
  if (!Number.isFinite(nowMs)) throw new Error('dmsMetricsDayId: nowMs must be finite');
  const d = new Date(nowMs);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Sum the existing-persisted counters with the in-process delta.
 * Pure — used at flush time so the doc grows monotonically across the
 * day.
 *
 * Tricky part: the in-process counters are themselves cumulative since
 * cold start, not deltas. Whatever counter values landed in the
 * persisted doc on the previous flush therefore ALREADY include the
 * in-process counter for that session up to that flush. The next
 * flush during the same session is NOT a delta — it's the new
 * cumulative for that session.
 *
 * Net rule: per-session monotonic = pick whichever is larger between
 * persisted and in-process for each counter. That keeps multi-tab
 * sessions correct (one tab's flush doesn't get clobbered by an
 * older tab's smaller counter), AND it's monotonic.
 */
export function mergeDmsMetricCounters(
  persisted: DmsMetricsDoc | null,
  inProcess: DocMatchStrictnessSummary,
  opts: { day: string; classifierVersion: string; nowIso: string },
): DmsMetricsDoc {
  const max = (a: number, b: number): number => Math.max(a | 0, b | 0);
  const pScoring = persisted?.scoring;
  const pApp = persisted?.applicability;
  return {
    day: opts.day,
    classifierVersion: opts.classifierVersion,
    scoring: {
      cacheHits:              max(pScoring?.cacheHits ?? 0,              inProcess.scoring.cacheHits),
      cacheMisses:            max(pScoring?.cacheMisses ?? 0,            inProcess.scoring.cacheMisses),
      timeouts:               max(pScoring?.timeouts ?? 0,               inProcess.scoring.timeouts),
      errors:                 max(pScoring?.errors ?? 0,                 inProcess.scoring.errors),
      filenameShortCircuits:  max(pScoring?.filenameShortCircuits ?? 0,  inProcess.scoring.filenameShortCircuits),
    },
    applicability: {
      filenameNegativeSuppressed: max(
        pApp?.filenameNegativeSuppressed ?? 0,
        inProcess.applicability.filenameNegativeSuppressed,
      ),
      strongClaimRejected: max(
        pApp?.strongClaimRejected ?? 0,
        inProcess.applicability.strongClaimRejected,
      ),
    },
    lastFlushedAt: opts.nowIso,
  };
}

/* ── Default Firestore I/O ───────────────────────────────────────── */

/**
 * Lazy default I/O — pulls `firebase/firestore` only when called.
 * Keeps the strictness pipeline tree-shake-friendly for tests that
 * never exercise the flush path.
 */
async function defaultIO(): Promise<DmsMetricsIO> {
  const { db } = await import('../../firebase');
  const fs = await import('firebase/firestore');
  return {
    getDoc: async (uid, dayId) => {
      const snap = await fs.getDoc(fs.doc(db, `users/${uid}/${DMS_METRICS_SUBCOLLECTION}`, dayId));
      return snap.exists() ? (snap.data() as DmsMetricsDoc) : null;
    },
    setDoc: async (uid, dayId, value) => {
      await fs.setDoc(
        fs.doc(db, `users/${uid}/${DMS_METRICS_SUBCOLLECTION}`, dayId),
        value,
        { merge: true },
      );
    },
    listDocs: async (uid, limit) => {
      const q = fs.query(
        fs.collection(db, `users/${uid}/${DMS_METRICS_SUBCOLLECTION}`),
        fs.orderBy('day', 'desc'),
        fs.limit(limit),
      );
      const snap = await fs.getDocs(q);
      // Return oldest→newest for chart-friendly consumers.
      return snap.docs
        .map(d => d.data() as DmsMetricsDoc)
        .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0));
    },
  };
}

/* ── Flush ───────────────────────────────────────────────────────── */

/**
 * Read the in-process strictness counters, merge with the persisted
 * doc for today, and write back. Returns the doc that was written
 * (or null on failure / no-uid).
 *
 * Safe to call from anywhere — wrapped in try/catch. The scheduler
 * (`scheduleDmsMetricsFlush`) is the production caller; tests can
 * also invoke this directly with a fake `io` to exercise the merge
 * path without timers.
 */
export async function flushDmsMetrics(opts: {
  uid: string;
  nowMs?: number;
  classifierVersion?: string;
  /** Override the in-process summary read. Tests inject deterministic
   *  counters; production omits and we read from telemetry singletons. */
  summary?: DocMatchStrictnessSummary;
  io?: DmsMetricsIO;
}): Promise<DmsMetricsDoc | null> {
  if (!opts.uid) return null;
  const nowMs = opts.nowMs ?? Date.now();
  const day = dmsMetricsDayId(nowMs);
  const classifierVersion = opts.classifierVersion ?? MODEL_VERSION;
  // Lazy-load the in-process summary reader so this module's static
  // graph stays free of `docApplicability` (see header comment).
  const inProcess = opts.summary
    ?? (await import('./strictnessTelemetry')).getDocMatchStrictnessSummary();
  try {
    const io = opts.io ?? (await defaultIO());
    const existing = await io.getDoc(opts.uid, day);
    const merged = mergeDmsMetricCounters(existing, inProcess, {
      day,
      classifierVersion,
      nowIso: new Date(nowMs).toISOString(),
    });
    await io.setDoc(opts.uid, day, merged);
    return merged;
  } catch (e) {
    // Telemetry MUST NOT throw — the strictness pipeline calls this.
    // Use console.warn (not console.debug, per CLAUDE.md convention).
    console.warn('[dms persistentMetrics] flush failed:', (e as Error)?.message);
    return null;
  }
}

/* ── Debounced scheduler ─────────────────────────────────────────── */

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let pendingUid: string | null = null;
let pendingIO: DmsMetricsIO | null = null;
let pendingDebounceMs: number = DMS_METRICS_FLUSH_DEBOUNCE_MS;

/**
 * Arm a single trailing-edge debounced flush. Calling this N times
 * inside the debounce window costs ONE Firestore write at the end.
 *
 * No-op when `uid` is falsy — the strictness pipeline can call this
 * unconditionally without a sign-in branch on every doc-match.
 *
 * Test seam: `io` and `debounceMs` are overrides. Production omits.
 */
export function scheduleDmsMetricsFlush(opts: {
  uid: string | null | undefined;
  io?: DmsMetricsIO;
  debounceMs?: number;
}): void {
  if (!opts.uid) return;
  pendingUid = opts.uid;
  if (opts.io) pendingIO = opts.io;
  if (typeof opts.debounceMs === 'number' && opts.debounceMs >= 0) {
    pendingDebounceMs = opts.debounceMs;
  }
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = setTimeout(() => {
    const uid = pendingUid;
    const io = pendingIO ?? undefined;
    pendingTimer = null;
    pendingUid = null;
    pendingIO = null;
    pendingDebounceMs = DMS_METRICS_FLUSH_DEBOUNCE_MS;
    if (!uid) return;
    void flushDmsMetrics({ uid, io }).catch(() => undefined);
  }, pendingDebounceMs);
}

/** Cancel any armed debounce. Used by tests + on logout. */
export function cancelPendingDmsMetricsFlush(): void {
  if (pendingTimer) clearTimeout(pendingTimer);
  pendingTimer = null;
  pendingUid = null;
  pendingIO = null;
  pendingDebounceMs = DMS_METRICS_FLUSH_DEBOUNCE_MS;
}

/* ── Loader (read-side) ──────────────────────────────────────────── */

/**
 * Load the last N days of metrics for `uid`, oldest→newest. Used by
 * the DA-1 admin page (DmsMisclassifiedPanel) to render the trend
 * delta. Returns [] on read failure.
 *
 * The page caller is responsible for admin RBAC — the Firestore rule
 * also enforces it (admins or owner). Mis-using this from a non-admin
 * surface returns [] rather than throwing.
 */
export async function loadDmsMetricsHistory(opts: {
  uid: string;
  days?: number;
  io?: DmsMetricsIO;
}): Promise<DmsMetricsDoc[]> {
  if (!opts.uid) return [];
  const limit = Math.max(1, Math.floor(opts.days ?? DMS_METRICS_DEFAULT_HISTORY_DAYS));
  try {
    const io = opts.io ?? (await defaultIO());
    return await io.listDocs(opts.uid, limit);
  } catch (e) {
    console.warn('[dms persistentMetrics] history load failed:', (e as Error)?.message);
    return [];
  }
}
